import {
  customElement,
  DeesElement,
  type TemplateResult,
  html,
  property,
  state,
  css,
  cssManager,
} from '@design.estate/dees-element';
import { demoFunc } from './dees-updater.demo.js';
import { cssGeistFontFamily, cssMonoFontFamily } from '../../00fonts.js';
import { zIndexRegistry } from '../../00zindex.js';
import '../dees-icon/dees-icon.js';

export type TDeesUpdaterSuccessAction = 'close' | 'reload';
export type TDeesUpdaterPhase =
  | 'checking'
  | 'downloading'
  | 'verifying'
  | 'installing'
  | 'restarting'
  | 'verifyingApp';

export type TDeesUpdaterMode = 'progress' | 'ready' | 'warning' | 'error';

export interface IDeesUpdaterPhaseDefinition {
  id: TDeesUpdaterPhase;
  label: string;
  description: string;
}

/**
 * Field-compatible replacement for the dees-stepper IStepProgressState the
 * updater previously relied on. Kept so existing callers of setPhase() /
 * updateProgress() continue to compile and behave the same.
 */
export interface IDeesUpdaterProgressState {
  label?: string;
  percentage?: number;
  indeterminate?: boolean;
  showPercentage?: boolean;
  statusText?: string;
  terminalLines?: string[];
  statusRows?: number;
}

export interface IDeesUpdaterPhaseUpdateOptions extends Partial<IDeesUpdaterProgressState> {
  line?: string;
}

export interface IDeesUpdaterWarningOptions {
  line?: string;
}

export interface IDeesUpdaterErrorOptions {
  line?: string;
}

export interface IDeesUpdaterOptions {
  appName?: string;
  appIcon?: string;
  currentVersion?: string;
  updatedVersion?: string;
  updateSource?: string;
  impactItems?: string[];
  phases?: IDeesUpdaterPhaseDefinition[];
  moreInfoUrl?: string;
  changelogUrl?: string;
  successAction?: TDeesUpdaterSuccessAction;
  successDelayMs?: number;
  successActionLabel?: string;
  maxTerminalLines?: number;
  onRetry?: () => Promise<void> | void;
  onSuccessAction?: () => Promise<void> | void;
}

const defaultPhaseDefinitions: IDeesUpdaterPhaseDefinition[] = [
  {
    id: 'checking',
    label: 'Check release',
    description: 'Resolve release metadata and confirm that the target version is available.',
  },
  {
    id: 'downloading',
    label: 'Download',
    description: 'Download the release payload from the configured update source.',
  },
  {
    id: 'verifying',
    label: 'Verify payload',
    description: 'Verify the downloaded payload before applying it.',
  },
  {
    id: 'installing',
    label: 'Install release',
    description: 'Install the new release on this host.',
  },
  {
    id: 'restarting',
    label: 'Restart app',
    description: 'Restart the application so the new release can become active.',
  },
  {
    id: 'verifyingApp',
    label: 'Verify app',
    description: 'Wait for the application to answer with the expected version.',
  },
];

const defaultImpactItems = [
  'The application can temporarily disconnect while services restart.',
  'The updater shows the current installer status for each phase.',
  'Do not close this browser tab until the updater reports a final state.',
];

declare global {
  interface HTMLElementTagNameMap {
    'dees-updater': DeesUpdater;
  }
}

@customElement('dees-updater')
export class DeesUpdater extends DeesElement {
  public static demo = demoFunc;
  public static demoGroups = ['Utility'];

  public static async createAndShow(optionsArg: IDeesUpdaterOptions = {}) {
    const updater = new DeesUpdater();
    updater.appName = optionsArg.appName ?? 'Application';
    updater.appIcon = optionsArg.appIcon ?? '';
    updater.currentVersion = optionsArg.currentVersion ?? '';
    updater.updatedVersion = optionsArg.updatedVersion ?? '';
    updater.updateSource = optionsArg.updateSource ?? '';
    updater.impactItems = optionsArg.impactItems ?? [...defaultImpactItems];
    updater.phases = optionsArg.phases ?? [...defaultPhaseDefinitions];
    updater.moreInfoUrl = optionsArg.moreInfoUrl ?? '';
    updater.changelogUrl = optionsArg.changelogUrl ?? '';
    updater.successAction = optionsArg.successAction ?? 'close';
    updater.successDelayMs = optionsArg.successDelayMs ?? 10000;
    updater.successActionLabel = optionsArg.successActionLabel ?? '';
    updater.maxTerminalLines = optionsArg.maxTerminalLines ?? 200;
    updater.onRetry = optionsArg.onRetry ?? null;
    updater.onSuccessAction = optionsArg.onSuccessAction ?? null;
    document.body.appendChild(updater);
    await updater.show();
    return updater;
  }

  @property({ type: String })
  accessor appName = 'Application';

  @property({ type: String })
  accessor appIcon = '';

  @property({ type: String })
  accessor currentVersion = '';

  @property({ type: String })
  accessor updatedVersion = '';

  @property({ type: String })
  accessor updateSource = '';

  @property({ type: Array })
  accessor impactItems: string[] = [...defaultImpactItems];

  @property({ type: Array })
  accessor phases: IDeesUpdaterPhaseDefinition[] = [...defaultPhaseDefinitions];

  @property({ type: String })
  accessor moreInfoUrl = '';

  @property({ type: String })
  accessor changelogUrl = '';

  @property({ type: String })
  accessor successAction: TDeesUpdaterSuccessAction = 'close';

  @property({ type: Number })
  accessor successDelayMs = 10000;

  @property({ type: String })
  accessor successActionLabel = '';

  @property({ type: Number })
  accessor maxTerminalLines = 200;

  // --- internal reactive state ---
  @state()
  accessor mode: TDeesUpdaterMode = 'progress';

  @state()
  accessor activePhaseIndex = 0;

  @state()
  accessor withinPhaseFraction = 0;

  @state()
  accessor indeterminate = true;

  @state()
  accessor statusLine = '';

  @state()
  accessor outcomeMessage = '';

  @state()
  accessor progressLines: string[] = [];

  @state()
  accessor detailsOpen = false;

  @state()
  accessor countdownRemainingMs = 0;

  @state()
  accessor allPhasesDone = false;

  @state()
  accessor visible = false;

  @state()
  accessor closing = false;

  @state()
  accessor backdropZIndex = 1000;

  private showPromise: Promise<void> | null = null;
  private successActionStarted = false;
  private countdownAbort: AbortController | null = null;
  private onRetry: (() => Promise<void> | void) | null = null;
  private onSuccessAction: (() => Promise<void> | void) | null = null;

  public static styles = [
    cssManager.defaultStyles,
    css`
      :host {
        /* System colors (dark appearance) */
        --upd-blue: #0a84ff;
        --upd-green: #30d158;
        --upd-amber: #ff9f0a;
        --upd-red: #ff453a;
        /* Label hierarchy on dark */
        --upd-text: #f5f5f7;
        --upd-text-muted: rgba(235, 235, 245, 0.6);
        --upd-text-dim: rgba(235, 235, 245, 0.3);
        --upd-track: rgba(255, 255, 255, 0.16);
        /* squircle silhouette (superellipse, n≈5) */
        --upd-squircle: polygon(
          100% 50%, 99.87% 70.83%, 99.49% 77.41%, 98.86% 82.1%, 97.96% 85.8%, 96.78% 88.85%,
          95.31% 91.39%, 93.53% 93.53%, 91.39% 95.31%, 88.85% 96.78%, 85.8% 97.96%, 82.1% 98.86%,
          77.41% 99.49%, 70.83% 99.87%, 50% 100%, 29.17% 99.87%, 22.59% 99.49%, 17.9% 98.86%,
          14.2% 97.96%, 11.15% 96.78%, 8.61% 95.31%, 6.47% 93.53%, 4.69% 91.39%, 3.22% 88.85%,
          2.04% 85.8%, 1.14% 82.1%, 0.51% 77.41%, 0.13% 70.83%, 0% 50%, 0.13% 29.17%, 0.51% 22.59%,
          1.14% 17.9%, 2.04% 14.2%, 3.22% 11.15%, 4.69% 8.61%, 6.47% 6.47%, 8.61% 4.69%,
          11.15% 3.22%, 14.2% 2.04%, 17.9% 1.14%, 22.59% 0.51%, 29.17% 0.13%, 50% 0%, 70.83% 0.13%,
          77.41% 0.51%, 82.1% 1.14%, 85.8% 2.04%, 88.85% 3.22%, 91.39% 4.69%, 93.53% 6.47%,
          95.31% 8.61%, 96.78% 11.15%, 97.96% 14.2%, 98.86% 17.9%, 99.49% 22.59%, 99.87% 29.17%
        );
        /* system UI font where available */
        font-family: system-ui, 'SF Pro Text',
          ${cssGeistFontFamily};
      }

      .backdrop {
        position: fixed;
        inset: 0;
        display: flex;
        align-items: safe center;
        justify-content: center;
        box-sizing: border-box;
        padding: 40px 32px;
        overflow-y: auto;
        background: radial-gradient(125% 125% at 50% 36%, #0c0c0f 0%, #000 76%);
        opacity: 0;
        transition: opacity 0.3s ease;
        -webkit-font-smoothing: antialiased;
        text-rendering: optimizeLegibility;
      }

      .backdrop.visible {
        opacity: 1;
      }

      .backdrop.closing {
        opacity: 0;
      }

      .column {
        width: 100%;
        max-width: 360px;
        display: flex;
        flex-direction: column;
        align-items: center;
        text-align: center;
        color: var(--upd-text);
        opacity: 0;
        transform: translateY(10px) scale(0.97);
        transition: transform 0.4s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.4s ease;
      }

      .backdrop.visible .column {
        opacity: 1;
        transform: none;
      }

      .backdrop.closing .column {
        opacity: 0;
        transform: translateY(6px) scale(0.99);
      }

      .mode-progress {
        --accent: var(--upd-blue);
      }
      .mode-ready {
        --accent: var(--upd-green);
      }
      .mode-warning {
        --accent: var(--upd-amber);
      }
      .mode-error {
        --accent: var(--upd-red);
      }

      /* --- app icon (squircle) --- */
      .iconWrap {
        position: relative;
        width: 76px;
        height: 76px;
        margin-bottom: 24px;
      }

      .iconTile {
        position: absolute;
        inset: 0;
        z-index: 1;
        clip-path: var(--upd-squircle);
        background: linear-gradient(180deg, rgba(255, 255, 255, 0.18) 0%, rgba(255, 255, 255, 0.05) 100%);
        filter: drop-shadow(0 10px 26px rgba(0, 0, 0, 0.55));
      }

      .iconGlyph {
        position: absolute;
        inset: 0;
        z-index: 2;
        display: grid;
        place-items: center;
        color: var(--upd-text);
        font-size: 32px;
        font-weight: 600;
      }

      .iconBadge {
        position: absolute;
        z-index: 3;
        right: -2px;
        bottom: -2px;
        width: 26px;
        height: 26px;
        border-radius: 50%;
        display: grid;
        place-items: center;
        color: #fff;
        background: var(--accent);
        box-shadow: 0 0 0 3px #000, 0 2px 6px rgba(0, 0, 0, 0.4);
      }

      /* --- title / subtitle --- */
      .title {
        font-size: 27px;
        font-weight: 600;
        letter-spacing: -0.022em;
        line-height: 1.18;
        color: var(--upd-text);
      }

      .subtitle {
        margin-top: 5px;
        font-size: 15px;
        font-weight: 400;
        letter-spacing: -0.01em;
        color: var(--upd-text-muted);
      }

      .subtitle.outcome {
        color: var(--accent);
        font-weight: 500;
      }

      /* --- progress bar --- */
      .bar {
        position: relative;
        width: 100%;
        height: 4px;
        margin-top: 28px;
        border-radius: 999px;
        background: var(--upd-track);
        overflow: hidden;
      }

      .barFill {
        position: absolute;
        left: 0;
        top: 0;
        bottom: 0;
        border-radius: 999px;
        background: var(--accent);
        transition: width 0.55s cubic-bezier(0.4, 0, 0.2, 1), background-color 0.4s ease;
      }

      .statusLine {
        margin-top: 14px;
        min-height: 16px;
        font-size: 13px;
        letter-spacing: -0.01em;
        color: var(--upd-text-muted);
      }

      .statusLine.outcome {
        color: var(--upd-text-muted);
        max-width: 320px;
        line-height: 1.5;
      }

      /* --- phase checklist --- */
      .checklist {
        margin-top: 26px;
        display: flex;
        flex-direction: column;
        gap: 11px;
        width: 100%;
        max-width: 224px;
      }

      .phaseRow {
        display: flex;
        align-items: center;
        gap: 10px;
        font-size: 13px;
        letter-spacing: -0.01em;
      }

      .glyph {
        flex: 0 0 auto;
        width: 16px;
        height: 16px;
        display: grid;
        place-items: center;
      }

      .glyph .ring {
        width: 12px;
        height: 12px;
        border-radius: 50%;
        box-shadow: inset 0 0 0 1.5px rgba(255, 255, 255, 0.22);
      }

      .glyph .check {
        width: 16px;
        height: 16px;
        border-radius: 50%;
        display: grid;
        place-items: center;
        color: #fff;
        background: var(--accent);
      }

      .glyph .spinner {
        width: 13px;
        height: 13px;
        border-radius: 50%;
        border: 1.5px solid rgba(255, 255, 255, 0.18);
        border-top-color: var(--accent);
        animation: spin 0.7s linear infinite;
      }

      .phaseLabel {
        text-align: left;
      }

      .phaseRow.pending .phaseLabel {
        color: var(--upd-text-dim);
      }

      .phaseRow.active .phaseLabel {
        color: var(--upd-text);
        font-weight: 500;
      }

      .phaseRow.done .phaseLabel {
        color: var(--upd-text-muted);
      }

      /* --- details disclosure --- */
      .details {
        width: 100%;
        margin-top: 22px;
      }

      .detailsToggle {
        appearance: none;
        border: none;
        background: transparent;
        color: var(--upd-text-dim);
        font-family: inherit;
        font-size: 12px;
        letter-spacing: -0.01em;
        cursor: pointer;
        display: inline-flex;
        align-items: center;
        gap: 5px;
        padding: 4px 8px;
        border-radius: 6px;
        transition: color 0.15s ease, background 0.15s ease;
      }

      .detailsToggle:hover {
        color: var(--upd-text-muted);
        background: rgba(255, 255, 255, 0.04);
      }

      .detailsToggle .chev {
        display: inline-flex;
        transition: transform 0.25s ease;
      }

      .detailsToggle.open .chev {
        transform: rotate(180deg);
      }

      .logPanel {
        overflow: hidden;
        max-height: 0;
        opacity: 0;
        transition: max-height 0.3s ease, opacity 0.3s ease, margin-top 0.3s ease;
      }

      .logPanel.open {
        max-height: 180px;
        opacity: 1;
        margin-top: 10px;
      }

      .logScroll {
        max-height: 180px;
        overflow-y: auto;
        text-align: left;
        padding: 12px 14px;
        border-radius: 10px;
        background: rgba(255, 255, 255, 0.04);
        box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.06);
        font-family: ${cssMonoFontFamily};
        font-size: 11.5px;
        line-height: 1.6;
        color: var(--upd-text-muted);
      }

      .logLine {
        white-space: pre-wrap;
        word-break: break-word;
      }

      /* --- footer --- */
      .links {
        margin-top: 24px;
        display: flex;
        gap: 18px;
        justify-content: center;
      }

      .link {
        appearance: none;
        border: none;
        background: transparent;
        color: var(--upd-blue);
        font-family: inherit;
        font-size: 14px;
        letter-spacing: -0.01em;
        cursor: pointer;
        padding: 2px 0;
        transition: color 0.15s ease;
      }

      .link:hover {
        color: color-mix(in srgb, var(--upd-blue) 78%, #fff);
      }

      .actions {
        margin-top: 24px;
        display: flex;
        gap: 12px;
        justify-content: center;
        flex-wrap: wrap;
      }

      .btn {
        appearance: none;
        font-family: inherit;
        font-size: 15px;
        font-weight: 590;
        letter-spacing: -0.01em;
        padding: 11px 22px;
        border-radius: 980px;
        cursor: pointer;
        border: none;
        background: rgba(255, 255, 255, 0.12);
        color: var(--upd-text);
        transition: background 0.15s ease, transform 0.08s ease, filter 0.15s ease;
      }

      .btn:hover {
        background: rgba(255, 255, 255, 0.18);
      }

      .btn:active {
        transform: scale(0.97);
        filter: brightness(0.92);
      }

      .btn.primary {
        background: var(--accent);
        color: #fff;
      }

      .btn.primary:hover {
        background: color-mix(in srgb, var(--accent) 90%, #fff);
      }

      @keyframes spin {
        to {
          transform: rotate(360deg);
        }
      }

      @media (prefers-reduced-motion: reduce) {
        .backdrop,
        .column,
        .barFill,
        .logPanel {
          transition: none;
        }
        .glyph .spinner {
          animation: none;
        }
      }
    `,
  ];

  public render(): TemplateResult {
    const showBar = this.mode === 'progress' || this.mode === 'ready';
    const showChecklist = this.mode === 'progress' || this.mode === 'ready';
    const overall = this.getOverallPercentage();
    return html`
      <div
        class="backdrop ${this.visible ? 'visible' : ''} ${this.closing ? 'closing' : ''}"
        style="z-index:${this.backdropZIndex}"
      >
        <div class="column mode-${this.mode}">
          ${this.renderIcon()}
          <div class="title">${this.appName}</div>
          <div class="subtitle ${this.mode === 'progress' ? '' : 'outcome'}">
            ${this.renderSubtitle()}
          </div>
          ${showBar
            ? html`<div class="bar">
                <div class="barFill" style="width:${overall}%"></div>
              </div>`
            : ''}
          ${this.renderBelowBarLine()}
          ${showChecklist ? this.renderChecklist() : ''}
          ${this.renderDetails()}
          ${this.renderLinks()}
          ${this.renderActions()}
        </div>
      </div>
    `;
  }

  public async connectedCallback(): Promise<void> {
    await super.connectedCallback();
    void this.show();
  }

  public async show(): Promise<void> {
    if (this.showPromise) {
      return this.showPromise;
    }
    this.showPromise = this.openUpdater();
    return this.showPromise;
  }

  public setPhase(
    phaseArg: TDeesUpdaterPhase,
    optionsArg: IDeesUpdaterPhaseUpdateOptions = {},
  ) {
    const index = this.getPhaseIndex(phaseArg);
    if (index < 0) {
      return;
    }

    this.mode = 'progress';
    this.allPhasesDone = false;
    this.activePhaseIndex = index;
    this.withinPhaseFraction = optionsArg.percentage != null
      ? this.clamp(optionsArg.percentage / 100, 0, 1)
      : 0;
    this.indeterminate = optionsArg.indeterminate ?? true;

    const phase = this.getPhaseDefinition(phaseArg);
    this.statusLine = optionsArg.statusText ?? `${phase.label}…`;

    if (optionsArg.terminalLines?.length) {
      this.appendLines(optionsArg.terminalLines);
    }
    if (optionsArg.line) {
      this.appendProgressLine(optionsArg.line);
    }
  }

  public updateProgress(progressStateArg: Partial<IDeesUpdaterProgressState>) {
    if (progressStateArg.percentage != null) {
      this.withinPhaseFraction = this.clamp(progressStateArg.percentage / 100, 0, 1);
    }
    if (progressStateArg.indeterminate != null) {
      this.indeterminate = progressStateArg.indeterminate;
    }
    if (progressStateArg.terminalLines?.length) {
      this.appendLines(progressStateArg.terminalLines);
    }

    const latest = progressStateArg.label
      ?? progressStateArg.statusText
      ?? progressStateArg.terminalLines?.at(-1);
    if (latest) {
      this.statusLine = latest;
    }
  }

  public appendProgressLine(lineArg: string) {
    this.progressLines = this.limitProgressLines([...this.progressLines, lineArg]);
    this.statusLine = lineArg;
  }

  public markUpdateWarning(messageArg: string, optionsArg: IDeesUpdaterWarningOptions = {}) {
    this.outcomeMessage = messageArg;
    this.indeterminate = false;
    this.appendProgressLine(optionsArg.line ?? `Warning: ${messageArg}`);
    this.statusLine = '';
    this.mode = 'warning';
    this.dispatchUpdaterEvent('deesUpdaterWarning', { message: messageArg });
  }

  public markUpdateError(messageArg: string, optionsArg: IDeesUpdaterErrorOptions = {}) {
    this.outcomeMessage = messageArg;
    this.indeterminate = false;
    this.appendProgressLine(optionsArg.line ?? `Error: ${messageArg}`);
    this.statusLine = '';
    this.mode = 'error';
    this.dispatchUpdaterEvent('deesUpdaterError', { message: messageArg });
  }

  public async markUpdateReady() {
    this.allPhasesDone = true;
    this.indeterminate = false;
    this.withinPhaseFraction = 1;
    this.appendProgressLine('Update verified');
    this.statusLine = '';
    this.mode = 'ready';
    this.dispatchUpdaterEvent('deesUpdaterReady', { version: this.updatedVersion });
    void this.runReadyCountdown();
  }

  public async destroy() {
    this.countdownAbort?.abort();
    this.closing = true;
    this.visible = false;
    await this.delay(320);

    if (this.parentElement) {
      this.parentElement.removeChild(this);
    }

    zIndexRegistry.unregister(this);
    this.dispatchUpdaterEvent('deesUpdaterClosed', {});
  }

  // --- rendering helpers ---

  private renderIcon(): TemplateResult {
    return html`
      <div class="iconWrap">
        <div class="iconTile"></div>
        <div class="iconGlyph">
          ${this.appIcon
            ? html`<dees-icon .icon=${this.appIcon} .iconSize=${34}></dees-icon>`
            : this.appName.slice(0, 1).toUpperCase()}
        </div>
        ${this.mode === 'progress' ? '' : html`<div class="iconBadge">${this.renderBadgeGlyph()}</div>`}
      </div>
    `;
  }

  private renderBadgeGlyph(): TemplateResult {
    if (this.mode === 'ready') {
      return this.renderCheckSvg(14);
    }
    if (this.mode === 'error') {
      return html`<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor"
        stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round">
        <path d="M18 6 6 18" /><path d="m6 6 12 12" />
      </svg>`;
    }
    // warning
    return html`<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor"
      stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round">
      <path d="M12 7.5v5.5" /><path d="M12 16.5h.01" />
    </svg>`;
  }

  private renderSubtitle(): string {
    switch (this.mode) {
      case 'ready':
        return 'Update complete';
      case 'warning':
        return 'Needs attention';
      case 'error':
        return 'Update failed';
      default:
        return this.updatedVersion ? `Updating to ${this.updatedVersion}` : 'Updating…';
    }
  }

  private renderBelowBarLine(): TemplateResult | '' {
    if (this.mode === 'ready') {
      return html`<div class="statusLine">${this.getReadyCountdownText()}</div>`;
    }
    if (this.mode === 'warning' || this.mode === 'error') {
      return this.outcomeMessage
        ? html`<div class="statusLine outcome">${this.outcomeMessage}</div>`
        : '';
    }
    return html`<div class="statusLine">${this.statusLine}</div>`;
  }

  private renderChecklist(): TemplateResult {
    return html`
      <div class="checklist">
        ${this.phases.map((phaseArg, indexArg) => {
          const phaseState = this.getPhaseState(indexArg);
          return html`
            <div class="phaseRow ${phaseState}">
              <span class="glyph">${this.renderPhaseGlyph(phaseState)}</span>
              <span class="phaseLabel">${phaseArg.label}</span>
            </div>
          `;
        })}
      </div>
    `;
  }

  private renderPhaseGlyph(stateArg: 'done' | 'active' | 'pending'): TemplateResult {
    if (stateArg === 'done') {
      return html`<span class="check">${this.renderCheckSvg(11)}</span>`;
    }
    if (stateArg === 'active') {
      return html`<span class="spinner"></span>`;
    }
    return html`<span class="ring"></span>`;
  }

  private renderDetails(): TemplateResult | '' {
    if (!this.progressLines.length) {
      return '';
    }
    return html`
      <div class="details">
        <button
          class="detailsToggle ${this.detailsOpen ? 'open' : ''}"
          @click=${() => (this.detailsOpen = !this.detailsOpen)}
        >
          Details
          <span class="chev">
            <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor"
              stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round">
              <path d="m6 9 6 6 6-6" />
            </svg>
          </span>
        </button>
        <div class="logPanel ${this.detailsOpen ? 'open' : ''}">
          <div class="logScroll">
            ${this.progressLines.map((lineArg) => html`<div class="logLine">${lineArg}</div>`)}
          </div>
        </div>
      </div>
    `;
  }

  private renderLinks(): TemplateResult | '' {
    const links: Array<{ name: string; url: string }> = [];
    if (this.moreInfoUrl) {
      links.push({ name: 'More info', url: this.moreInfoUrl });
    }
    if (this.changelogUrl) {
      links.push({ name: 'Changelog', url: this.changelogUrl });
    }
    if (!links.length) {
      return '';
    }
    return html`
      <div class="links">
        ${links.map(
          (linkArg) => html`<button class="link" @click=${() => this.openExternalUrl(linkArg.url)}>
            ${linkArg.name}
          </button>`,
        )}
      </div>
    `;
  }

  private renderActions(): TemplateResult | '' {
    const actions = this.getActionDescriptors();
    if (!actions.length) {
      return '';
    }
    return html`
      <div class="actions">
        ${actions.map(
          (actionArg) => html`<button
            class="btn ${actionArg.primary ? 'primary' : ''}"
            @click=${() => actionArg.action()}
          >
            ${actionArg.label}
          </button>`,
        )}
      </div>
    `;
  }

  private renderCheckSvg(sizeArg: number): TemplateResult {
    return html`<svg viewBox="0 0 24 24" width="${sizeArg}" height="${sizeArg}" fill="none"
      stroke="currentColor" stroke-width="3.2" stroke-linecap="round" stroke-linejoin="round">
      <path d="M20 6 9 17l-5-5" />
    </svg>`;
  }

  // --- state derivation ---

  private async openUpdater(): Promise<void> {
    this.backdropZIndex = zIndexRegistry.getNextZIndex();
    zIndexRegistry.register(this, this.backdropZIndex);

    // Only seed the initial status line — never clobber state a consumer may
    // already be driving (mode / phase / fractions keep their accessor
    // defaults until setPhase() etc. are called).
    if (!this.statusLine && this.mode === 'progress') {
      const firstPhase = this.phases[0];
      this.statusLine = firstPhase ? `${firstPhase.label}…` : 'Preparing update…';
    }

    await this.updateComplete;
    await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
    this.visible = true;
  }

  private getPhaseState(indexArg: number): 'done' | 'active' | 'pending' {
    if (this.allPhasesDone || this.mode === 'ready') {
      return 'done';
    }
    if (indexArg < this.activePhaseIndex) {
      return 'done';
    }
    if (indexArg === this.activePhaseIndex) {
      return 'active';
    }
    return 'pending';
  }

  private getOverallPercentage(): number {
    if (this.mode === 'ready' || this.allPhasesDone) {
      return 100;
    }
    const total = Math.max(1, this.phases.length);
    const index = this.clamp(this.activePhaseIndex, 0, total - 1);
    const within = this.clamp(this.withinPhaseFraction, 0, 1);
    const percentage = ((index + within) / total) * 100;
    return this.clamp(percentage, 4, 100);
  }

  private getActionDescriptors(): Array<{ label: string; primary: boolean; action: () => void | Promise<void> }> {
    switch (this.mode) {
      case 'ready':
        return [
          {
            label: this.getSuccessActionButtonLabel(),
            primary: true,
            action: () => this.runConfiguredSuccessAction(),
          },
        ];
      case 'warning': {
        const canContinue = this.successAction === 'reload' || !!this.onSuccessAction;
        if (canContinue) {
          return [
            { label: 'Close', primary: false, action: () => this.destroy() },
            {
              label: this.getSuccessActionButtonLabel(),
              primary: true,
              action: () => this.runConfiguredSuccessAction(),
            },
          ];
        }
        return [{ label: 'Close', primary: true, action: () => this.destroy() }];
      }
      case 'error': {
        if (this.onRetry) {
          return [
            { label: 'Close', primary: false, action: () => this.destroy() },
            { label: 'Retry', primary: true, action: async () => { await this.onRetry?.(); } },
          ];
        }
        return [{ label: 'Close', primary: true, action: () => this.destroy() }];
      }
      default:
        return [];
    }
  }

  // --- success countdown ---

  private async runReadyCountdown(): Promise<void> {
    this.countdownAbort?.abort();
    const abortController = new AbortController();
    this.countdownAbort = abortController;
    const totalDuration = Math.max(1000, this.successDelayMs);
    const startTime = Date.now();
    this.countdownRemainingMs = totalDuration;

    while (!abortController.signal.aborted) {
      const elapsed = Math.min(totalDuration, Date.now() - startTime);
      this.countdownRemainingMs = Math.max(0, totalDuration - elapsed);
      if (this.countdownRemainingMs <= 0) {
        break;
      }
      const completed = await this.delay(100, abortController.signal);
      if (!completed) {
        return;
      }
    }

    if (abortController.signal.aborted) {
      return;
    }
    await this.runConfiguredSuccessAction();
  }

  private async runConfiguredSuccessAction(): Promise<void> {
    if (this.successActionStarted) {
      return;
    }

    this.successActionStarted = true;
    this.countdownAbort?.abort();
    this.dispatchUpdaterEvent('deesUpdaterSuccessAction', {
      action: this.successAction,
      version: this.updatedVersion,
    });

    if (this.onSuccessAction) {
      await this.onSuccessAction();
      return;
    }

    if (this.successAction === 'reload') {
      await this.destroy();
      window.location.reload();
      return;
    }

    await this.destroy();
  }

  // --- label helpers ---

  private getSuccessActionDisplayLabel(): string {
    if (this.successActionLabel) {
      return this.successActionLabel;
    }
    if (this.onSuccessAction) {
      return 'Continuing automatically';
    }
    if (this.successAction === 'reload') {
      return 'Reloading application';
    }
    return 'Closing updater';
  }

  private getSuccessActionButtonLabel(): string {
    if (this.onSuccessAction) {
      return 'Continue now';
    }
    if (this.successAction === 'reload') {
      return 'Reload now';
    }
    return 'Close now';
  }

  private getSuccessCountdownLabel(secondsArg: number): string {
    return `${this.getSuccessActionDisplayLabel()} in ${secondsArg}s`;
  }

  private getSuccessActionNowLabel(): string {
    return `${this.getSuccessActionDisplayLabel()} now…`;
  }

  private getReadyCountdownText(): string {
    if (this.successActionStarted) {
      return this.getSuccessActionNowLabel();
    }
    const remainingSeconds = Math.max(0, Math.ceil(this.countdownRemainingMs / 1000));
    if (remainingSeconds > 0) {
      return this.getSuccessCountdownLabel(remainingSeconds);
    }
    return this.getSuccessActionNowLabel();
  }

  // --- misc helpers ---

  private appendLines(linesArg: string[]) {
    this.progressLines = this.limitProgressLines([...this.progressLines, ...linesArg]);
    const last = linesArg.at(-1);
    if (last) {
      this.statusLine = last;
    }
  }

  private limitProgressLines(linesArg: string[]): string[] {
    const maxLines = Math.max(1, Math.floor(this.maxTerminalLines || 200));
    return linesArg.slice(-maxLines);
  }

  private getPhaseDefinition(phaseArg: TDeesUpdaterPhase): IDeesUpdaterPhaseDefinition {
    return this.phases.find((phaseDefinitionArg) => phaseDefinitionArg.id === phaseArg)
      ?? defaultPhaseDefinitions.find((phaseDefinitionArg) => phaseDefinitionArg.id === phaseArg)
      ?? defaultPhaseDefinitions[0];
  }

  private getPhaseIndex(phaseArg: TDeesUpdaterPhase): number {
    return this.phases.findIndex((phaseDefinitionArg) => phaseDefinitionArg.id === phaseArg);
  }

  private clamp(valueArg: number, minArg: number, maxArg: number): number {
    return Math.min(maxArg, Math.max(minArg, valueArg));
  }

  private delay(timeoutArg: number, signal?: AbortSignal): Promise<boolean> {
    return new Promise((resolve) => {
      let completed = false;
      const finish = (result: boolean) => {
        if (completed) {
          return;
        }
        completed = true;
        signal?.removeEventListener('abort', handleAbort);
        resolve(result);
      };
      const handleAbort = () => {
        window.clearTimeout(timeoutId);
        finish(false);
      };
      const timeoutId = window.setTimeout(() => finish(true), timeoutArg);
      signal?.addEventListener('abort', handleAbort, { once: true });
    });
  }

  private openExternalUrl(urlArg: string) {
    window.open(urlArg, '_blank', 'noopener,noreferrer');
  }

  private dispatchUpdaterEvent(typeArg: string, detailArg: Record<string, unknown>) {
    this.dispatchEvent(new CustomEvent(typeArg, {
      detail: detailArg,
      bubbles: true,
      composed: true,
    }));
  }
}
